import React, { useState, useMemo } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { EngagementMeeting } from '../../../slices/marketplace/meetings/models/get-engagement-conference';
import {
    MeetingParticipantsTypesOptions,
    EngagementMeetingTypeHeaderColor,
    EngagementMeetingTypeBodyColor,
} from '../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingParticipantsType } from '../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { EngagementMeetingStatus } from '../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import CopyMeetingLinkPopup from './CopyMeetingLinkPopup';
import { useStateSelector } from '../../../store/selectors';
import { useNavigate } from 'react-router-dom';
import { QueryParams } from '../../../utils/queryParams';
import '../../../containers/PracticeManagementGroups/Conferences/Conferences.scss';
import './MeetingTile.scss';
import createNotification from '../../../utils/createNotification';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import { MarketplaceCommsHubTab } from '../../../containers/AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { SellerMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { BuyerOppMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import {
    faCommentDots,
    faCopy,
    faDownload,
    faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import InPersonMeetingParticipantsModal from '../../../containers/ConnectMarketplaceDashboard/EngagementMeetingsScheduling/InPersonMeetingParticipantsModal/InPersonMeetingParticipantsModal';
import { canBuyerTeamMemberManageMeetings, canSellerTeamMemberManageMeetings } from '../../../utils/meetingSchedulingHelper';

interface MeetingTileProps {
    engagementId?: number;
    engagementProcess: EngagementProcess;
    meeting: EngagementMeeting;
    party: TimelineParty;
    onJoinMeetingClick(meeting: EngagementMeeting): void;
    onDownloadFile?(eventId: number, fileId: number): void;
    getNotificationFlagCode?(meeting: EngagementMeeting): string;
    isReadOnlyMode?: boolean;
    isSfas?: boolean;
}

export const MeetingTile = (props: MeetingTileProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const navigate = useNavigate();
    const party = useCurrentMarketplaceParty(props.engagementId);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentTimeline = useStateSelector(
        (state: any) => state.timelines?.currentTimeline
    );
    const [isCreatingDiscussion, setIsCreatingDiscussion] =
        React.useState(false);

    const [isCopyLinkModalVisible, setIsCopyLinkModalVisible] = useState(false);
    const [isParticipantsModalVisible, setIsParticipantsModalVisible] =
        useState(false);

    const proposalId = useMemo(() => {
        if (
            !currentTimeline?.engagementData?.meetingProposals ||
            !props.meeting.engagementMeetingId
        ) {
            return undefined;
        }
        const proposal = currentTimeline.engagementData.meetingProposals.find(
            (p: any) => p.meetingId === props.meeting.engagementMeetingId
        );
        return proposal?.id;
    }, [currentTimeline, props.meeting.engagementMeetingId]);

    const isIn15Minutes = (date: Date) => {
        let currentDate = new Date();
        currentDate.setTime(currentDate.getTime() + 60 * 15 * 1000);
        return new Date(date) <= currentDate;
    };

    const getNotificationFlag = () => {
        if (props.getNotificationFlagCode) {
            const flagCode = props.getNotificationFlagCode(props.meeting);
            return (
                <NotificationFlag
                    flagClassName="nf-flag"
                    eventCode={flagCode}
                />
            );
        }
        return null;
    };

    const getMeetingTypeLabel = () => {
        return (
            MeetingParticipantsTypesOptions[props.meeting.participantsType] ||
            'Meeting'
        );
    };

    const getMeetingFormatLabel = () => {
        return MeetingFormatTypesOptions[props.meeting.format] || 'Virtual';
    };

    const getParticipants = () => {
        return props.meeting.participants || [];
    };

    const getParticipantsLabel = () => {
        return 'Participants';
    };

    const getHeaderColor = () => {
        return EngagementMeetingTypeHeaderColor[props.meeting.participantsType];
    };

    const getBodyColor = () => {
        return EngagementMeetingTypeBodyColor[props.meeting.participantsType];
    };

    const shouldShowJoinMeeting = () => {
        if (props.meeting.format === MeetingFormat.InPerson) {
            return false;
        }

        const isAdmin = props.party === TimelineParty.VetValue;

        if (isAdmin) {
            const adminSupportTypes = [
                MeetingParticipantsType.SellerAdminSupport,
                MeetingParticipantsType.BuyerAdminSupport,
                MeetingParticipantsType.AdvisorAdminSupport,
            ];
            return adminSupportTypes.includes(props.meeting.participantsType);
        }

        return true;
    };

    const shouldShowMessageIcon = () => {
        const isSellerBuyerMeeting =
            props.meeting.participantsType ===
                MeetingParticipantsType.SellerBuyer &&
            (props.party === TimelineParty.Seller ||
                props.party === TimelineParty.Buyer ||
                props.party === TimelineParty.Broker) &&
            !(props.isSfas && (props.party === TimelineParty.Seller || !props.engagementProcess?.broker?.isActive));

        const isSellerAdvisorMeeting =
            props.meeting.participantsType ===
                MeetingParticipantsType.SellerAdvisor &&
            (props.party === TimelineParty.Seller ||
                props.party === TimelineParty.Broker);

        return isSellerBuyerMeeting || isSellerAdvisorMeeting;
    };

    const shouldShowParticipantsIcon = () => {
        const isSellerBuyerMeeting =
            props.meeting.participantsType ===
            MeetingParticipantsType.SellerBuyer;

        const isInPersonOrHybridMeeting =
            props.meeting.format === MeetingFormat.InPerson ||
            props.meeting.format === MeetingFormat.Mixed;

        return isSellerBuyerMeeting && isInPersonOrHybridMeeting;
    };

    const shouldShowEditIcon = () => {
        const isSellerBuyerMeeting =
            props.meeting.participantsType ===
                MeetingParticipantsType.SellerBuyer &&
            (props.party === TimelineParty.Seller ||
                props.party === TimelineParty.Buyer ||
                props.party === TimelineParty.Broker);

        const isSellerOrBuyerParty =
            props.party === TimelineParty.Seller ||
            props.party === TimelineParty.Buyer;

        const canManageMeetings = (canBuyerTeamMemberManageMeetings(
            summary,
            props.engagementId
        ) || canSellerTeamMemberManageMeetings(summary, props.engagementId));

        return isSellerBuyerMeeting && isSellerOrBuyerParty && canManageMeetings;
    };

    const handleMessageIconClick = async (
        participantsType: MeetingParticipantsType
    ) => {
        if (participantsType === MeetingParticipantsType.SellerAdvisor) {
            if (props.meeting.threadedDiscussionId) {
                navigateToDocumentDiscussion(
                    props.meeting.commsId,
                    props.meeting.threadedDiscussionId,
                    participantsType
                );
            } else {
                if (isCreatingDiscussion) {
                    return;
                }
                setIsCreatingDiscussion(true);
                try {
                    const response = await axios.post(
                        `api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${props.meeting.engagementMeetingId}/discussion`
                    );
                    if (response && response.status === 200) {
                        const discussionId = response.data;
                        const brokerChatId =
                            props.engagementProcess.broker?.brokerChatId;
                        navigateToDocumentDiscussion(
                            brokerChatId,
                            discussionId,
                            participantsType
                        );
                    } else {
                        createNotification(
                            'An error occurred while creating discussion',
                            'error'
                        );
                    }
                } catch (e) {
                    createNotification(
                        'An error occurred while creating discussion',
                        'error'
                    );
                } finally {
                    setIsCreatingDiscussion(false);
                }
            }
        }
        if (participantsType === MeetingParticipantsType.SellerBuyer) {
            navigateToDocumentDiscussion(
                props.meeting.commsId,
                props.meeting.threadedDiscussionId,
                participantsType
            );
        }
    };

    const navigateToDocumentDiscussion = async (
        commsId: number,
        discussionId: number,
        participantsType: MeetingParticipantsType
    ) => {
        if (participantsType === MeetingParticipantsType.SellerAdvisor) {
            navigate(
                `/marketplace/engagement/${props.engagementId}?tab=${SellerMarketplaceTab.CommsHub}&subtab=${MarketplaceCommsHubTab.BrokerChat}`,
                {
                    state: {
                        discussionId: discussionId,
                    },
                }
            );
        }
        if (participantsType === MeetingParticipantsType.SellerBuyer) {
            const commsHubTab =
                party === TimelineParty.Buyer
                    ? BuyerOppMarketplaceTab.CommsHub
                    : SellerMarketplaceTab.CommsHub;

            if (party === TimelineParty.Buyer) {
                navigate(
                    `/opportunities?opportunityId=${props.engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
                );
            } else {
                navigate(
                    `/marketplace/engagement/${props.engagementId}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
                );
            }
        }
    };

    const navigateToMeetingScheduling = (meetingEventId: number) => {
        sessionStorage.setItem('scrollToMeetingId', meetingEventId.toString());

        const commsHubTab =
            party === TimelineParty.Buyer
                ? BuyerOppMarketplaceTab.CommsHub
                : SellerMarketplaceTab.CommsHub;

        if (party === TimelineParty.Buyer) {
            navigate(
                `/opportunities?opportunityId=${props.engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.MeetingScheduling}`
            );
        } else {
            navigate(
                `/marketplace/engagement/${props.engagementId}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.MeetingScheduling}`
            );
        }
    };

    const shouldShowMeetingCopyIcon = () => {
        const isValidStatus =
            props.meeting.status === EngagementMeetingStatus.Upcoming ||
            props.meeting.status === EngagementMeetingStatus.InProgress;

        if (!isValidStatus) {
            return false;
        }

        if (props.party === TimelineParty.Broker) {
            return (
                props.meeting.participantsType ===
                MeetingParticipantsType.SellerAdvisor
            );
        }

        if (props.party === TimelineParty.VetValue) {
            const adminSupportTypes = [
                MeetingParticipantsType.SellerAdminSupport,
                MeetingParticipantsType.BuyerAdminSupport,
                MeetingParticipantsType.AdvisorAdminSupport,
            ];
            return adminSupportTypes.includes(props.meeting.participantsType);
        }

        return false;
    };

    return (
        <div
            className="engagement-meeting-tile conference-box"
            style={{
                backgroundColor: getBodyColor(),
            }}>
            {getNotificationFlag()}
            <div
                className="conference-header"
                style={{
                    backgroundColor: getHeaderColor(),
                }}>
                <div>{props.meeting.name}</div>
                {shouldShowMeetingCopyIcon() && (
                    <FontAwesomeIcon
                        className="copy-meeting-icon"
                        onClick={() => setIsCopyLinkModalVisible(true)}
                        icon={faCopy}
                    />
                )}
            </div>
            <div className="conference-info">
                <div>
                    <Row className="row-space">
                        <Col md={12}>
                            <div>
                                <span className="label-text">
                                    Meeting Type:
                                </span>{' '}
                                {getMeetingTypeLabel()}
                            </div>
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={12}>
                            <div>
                                <span className="label-text">Format:</span>{' '}
                                {getMeetingFormatLabel()}
                            </div>
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col className="date-label" md={2}>
                            <div>
                                <span className="label-text">
                                    Start Date:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="date-value-text">
                            {props.meeting.dateStart
                                ? moment(props.meeting.dateStart).format(
                                      'MM/DD/YYYY'
                                  )
                                : 'N/A'}
                        </Col>
                        <Col className="time-label" md={2}>
                            <div>
                                <span className="label-text">
                                    Start Time:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="time-value-text">
                            {props.meeting.dateStart
                                ? moment(props.meeting.dateStart).format(
                                      'h:mm A'
                                  )
                                : 'N/A'}
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col className="date-label" md={2}>
                            <div>
                                <span className="label-text">
                                    End Date:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="date-value-text">
                            {props.meeting.dateEnd
                                ? moment(props.meeting.dateEnd).format(
                                      'MM/DD/YYYY'
                                  )
                                : 'N/A'}
                        </Col>
                        <Col className="time-label" md={2}>
                            <div>
                                <span className="label-text">
                                    End Time:
                                </span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="time-value-text">
                            {props.meeting.dateEnd
                                ? moment(props.meeting.dateEnd).format('h:mm A')
                                : 'N/A'}
                        </Col>
                    </Row>
                    {getParticipants().length > 0 && (
                        <Row className="row-space">
                            <Col md={12}>
                                <p className="label-text mb-2 mt-2">
                                    {getParticipantsLabel()}:
                                </p>
                                <p>
                                    {getParticipants()
                                        .map((p) => p.name)
                                        .join(', ')}
                                </p>
                            </Col>
                        </Row>
                    )}
                    <Row className="row-space">
                        <Col md={12}>
                            <p className="label-text mb-2 mt-2">Message:</p>
                            <p className="message">{props.meeting.message}</p>
                        </Col>
                    </Row>
                    {props.meeting.files?.length > 0 ? (
                        <Row>
                            <Col md={12}>
                                <p className="label-text mb-2">Documents:</p>
                            </Col>
                            {props.meeting.files.map((file, index) => {
                                return (
                                    <Col md={12} key={'file_' + index}>
                                        <p className="file">
                                            {file.name}{' '}
                                            {props.onDownloadFile &&
                                                !props.isReadOnlyMode && (
                                                    <FontAwesomeIcon
                                                        className="icon"
                                                        icon={faDownload}
                                                        onClick={() => {
                                                            props.onDownloadFile(
                                                                Number(
                                                                    props
                                                                        .meeting
                                                                        .id
                                                                ),
                                                                file.id
                                                            );
                                                        }}></FontAwesomeIcon>
                                                )}
                                        </p>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        []
                    )}
                </div>
                <Row>
                    <Col md={6}>
                        {props.meeting.dateStart &&
                        isIn15Minutes(props.meeting.dateStart) &&
                        shouldShowJoinMeeting() &&
                        !props.isReadOnlyMode ? (
                            <Button
                                color="primary"
                                onClick={() => {
                                    props.onJoinMeetingClick(props.meeting);
                                }}
                                className="btn-submit join-meeting-button">
                                Join Meeting
                            </Button>
                        ) : (
                            <div></div>
                        )}
                    </Col>
                    <Col md={6} className="icons-wrapper">
                        {shouldShowParticipantsIcon() && (
                            <FontAwesomeIcon
                                icon={faUser}
                                className="participants-icon"
                                onClick={() =>
                                    setIsParticipantsModalVisible(true)
                                }
                            />
                        )}
                        {shouldShowMessageIcon() && (
                            <FontAwesomeIcon
                                icon={faCommentDots}
                                className="message-icon"
                                onClick={
                                    isCreatingDiscussion
                                        ? undefined
                                        : () =>
                                              handleMessageIconClick(
                                                  props.meeting.participantsType
                                              )
                                }
                            />
                        )}
                        {shouldShowEditIcon() && (
                            <FontAwesomeIcon
                                icon={faEdit}
                                className="edit-icon"
                                onClick={() =>
                                    navigateToMeetingScheduling(props.meeting.id)
                                }
                            />
                        )}
                    </Col>
                </Row>
            </div>
            <CopyMeetingLinkPopup
                engagementId={props.engagementId || 0}
                meetingId={props.meeting.id}
                isVisible={isCopyLinkModalVisible}
                onClose={() => setIsCopyLinkModalVisible(false)}
            />
            <InPersonMeetingParticipantsModal
                isVisible={isParticipantsModalVisible}
                onClose={() => {
                    setIsParticipantsModalVisible(false);
                }}
                engagementId={props.engagementId}
                meetingId={proposalId}
                isReadOnly={true}
            />
        </div>
    );
};

export default MeetingTile;
